/**
 * User Service
 * 
 * Handles all user-related operations with Firestore including
 * user creation, profile management, and preferences.
 */

import { serverTimestamp } from 'firebase/firestore';
import {
  setDocument,
  getDocument,
  updateDocument,
  listenToDocument
} from '@/firebase/firestore';
import {
  FirebaseUser,
  CreateUserData,
  FIREBASE_COLLECTIONS,
  DEFAULT_USER_PREFERENCES,
  DEFAULT_USER_STATS,
  type FirebaseServiceResponse,
  type UserListener
} from '@/types/firebase';

/**
 * Create a new user document in Firestore
 */
export const createUser = async (
  uid: string,
  userData: CreateUserData
): Promise<FirebaseServiceResponse<FirebaseUser>> => {
  try {
    console.log('👤 Creating user document:', uid);
    
    const newUser: FirebaseUser = {
      uid,
      email: userData.email,
      displayName: userData.displayName,
      photoURL: userData.photoURL,
      emailVerified: userData.emailVerified,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
      
      profile: {
        firstName: userData.profile?.firstName || '',
        lastName: userData.profile?.lastName || '',
        timezone: userData.profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: userData.profile?.language || navigator.language || 'en'
      },
      
      preferences: {
        ...DEFAULT_USER_PREFERENCES,
        ...userData.preferences
      },
      
      overallStats: { ...DEFAULT_USER_STATS },
      
      lastSession: null
    };
    
    const result = await setDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      newUser
    );
    
    if (result.success) {
      console.log('✅ User created successfully:', uid);
      return {
        success: true,
        data: result.data as FirebaseUser
      };
    } else {
      console.error('🚫 Failed to create user:', result.error);
      return {
        success: false,
        error: {
          code: 'user/creation-failed',
          message: result.error || 'Failed to create user'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error creating user:', error);
    return {
      success: false,
      error: {
        code: 'user/creation-error',
        message: error instanceof Error ? error.message : 'Unknown error creating user'
      }
    };
  }
};

/**
 * Get user document from Firestore
 */
export const getUser = async (uid: string): Promise<FirebaseServiceResponse<FirebaseUser>> => {
  try {
    console.log('👤 Getting user document:', uid);
    
    const result = await getDocument<FirebaseUser>(
      FIREBASE_COLLECTIONS.USERS,
      uid
    );
    
    if (result.success && result.data) {
      console.log('✅ User retrieved successfully:', uid);
      return {
        success: true,
        data: result.data
      };
    } else {
      console.log('📭 User not found:', uid);
      return {
        success: false,
        error: {
          code: 'user/not-found',
          message: 'User document not found'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error getting user:', error);
    return {
      success: false,
      error: {
        code: 'user/fetch-error',
        message: error instanceof Error ? error.message : 'Unknown error fetching user'
      }
    };
  }
};

/**
 * Update user profile information
 */
export const updateUserProfile = async (
  uid: string,
  profileData: Partial<FirebaseUser['profile']>
): Promise<FirebaseServiceResponse<FirebaseUser['profile']>> => {
  try {
    console.log('👤 Updating user profile:', uid);
    
    const updateData = {
      profile: profileData,
      updatedAt: serverTimestamp()
    };
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      updateData
    );
    
    if (result.success) {
      console.log('✅ User profile updated successfully:', uid);
      return {
        success: true,
        data: profileData
      };
    } else {
      console.error('🚫 Failed to update user profile:', result.error);
      return {
        success: false,
        error: {
          code: 'user/profile-update-failed',
          message: result.error || 'Failed to update profile'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error updating user profile:', error);
    return {
      success: false,
      error: {
        code: 'user/profile-update-error',
        message: error instanceof Error ? error.message : 'Unknown error updating profile'
      }
    };
  }
};

/**
 * Update user preferences
 */
export const updateUserPreferences = async (
  uid: string,
  preferences: Partial<FirebaseUser['preferences']>
): Promise<FirebaseServiceResponse<FirebaseUser['preferences']>> => {
  try {
    console.log('👤 Updating user preferences:', uid);
    
    const updateData = {
      preferences,
      updatedAt: serverTimestamp()
    };
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      updateData
    );
    
    if (result.success) {
      console.log('✅ User preferences updated successfully:', uid);
      return {
        success: true,
        data: preferences as FirebaseUser['preferences']
      };
    } else {
      console.error('🚫 Failed to update user preferences:', result.error);
      return {
        success: false,
        error: {
          code: 'user/preferences-update-failed',
          message: result.error || 'Failed to update preferences'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error updating user preferences:', error);
    return {
      success: false,
      error: {
        code: 'user/preferences-update-error',
        message: error instanceof Error ? error.message : 'Unknown error updating preferences'
      }
    };
  }
};

/**
 * Update user overall statistics
 */
export const updateUserStats = async (
  uid: string,
  stats: Partial<FirebaseUser['overallStats']>
): Promise<FirebaseServiceResponse<FirebaseUser['overallStats']>> => {
  try {
    console.log('👤 Updating user stats:', uid);
    
    const updateData = {
      overallStats: stats,
      updatedAt: serverTimestamp()
    };
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      updateData
    );
    
    if (result.success) {
      console.log('✅ User stats updated successfully:', uid);
      return {
        success: true,
        data: stats as FirebaseUser['overallStats']
      };
    } else {
      console.error('🚫 Failed to update user stats:', result.error);
      return {
        success: false,
        error: {
          code: 'user/stats-update-failed',
          message: result.error || 'Failed to update stats'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error updating user stats:', error);
    return {
      success: false,
      error: {
        code: 'user/stats-update-error',
        message: error instanceof Error ? error.message : 'Unknown error updating stats'
      }
    };
  }
};

/**
 * Update user's last session information
 */
export const updateLastSession = async (
  uid: string,
  sessionInfo: FirebaseUser['lastSession']
): Promise<FirebaseServiceResponse<FirebaseUser['lastSession']>> => {
  try {
    console.log('👤 Updating last session:', uid);
    
    const updateData = {
      lastSession: sessionInfo,
      updatedAt: serverTimestamp()
    };
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      updateData
    );
    
    if (result.success) {
      console.log('✅ Last session updated successfully:', uid);
      return {
        success: true,
        data: sessionInfo
      };
    } else {
      console.error('🚫 Failed to update last session:', result.error);
      return {
        success: false,
        error: {
          code: 'user/last-session-update-failed',
          message: result.error || 'Failed to update last session'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error updating last session:', error);
    return {
      success: false,
      error: {
        code: 'user/last-session-update-error',
        message: error instanceof Error ? error.message : 'Unknown error updating last session'
      }
    };
  }
};

/**
 * Get or create user (used on login)
 */
export const getOrCreateUser = async (
  uid: string,
  userData: CreateUserData
): Promise<FirebaseServiceResponse<FirebaseUser>> => {
  try {
    console.log('👤 Getting or creating user:', uid);
    
    // First, try to get existing user
    const existingUser = await getUser(uid);
    
    if (existingUser.success && existingUser.data) {
      console.log('✅ Existing user found:', uid);
      return existingUser;
    }
    
    // User doesn't exist, create new one
    console.log('👤 User not found, creating new user:', uid);
    return await createUser(uid, userData);
    
  } catch (error) {
    console.error('🚫 Error in getOrCreateUser:', error);
    return {
      success: false,
      error: {
        code: 'user/get-or-create-error',
        message: error instanceof Error ? error.message : 'Unknown error in get or create user'
      }
    };
  }
};

/**
 * Listen to real-time user changes
 */
export const listenToUser = (
  uid: string,
  callback: UserListener
): (() => void) => {
  console.log('👂 Setting up user listener:', uid);
  
  return listenToDocument<FirebaseUser>(
    FIREBASE_COLLECTIONS.USERS,
    uid,
    (userData, error) => {
      if (error) {
        console.error('🚫 User listener error:', error);
        callback(null, {
          code: 'user/listener-error',
          message: error
        });
      } else {
        console.log('👂 User data updated:', uid);
        callback(userData);
      }
    }
  );
};

/**
 * Check if user exists
 */
export const userExists = async (uid: string): Promise<boolean> => {
  try {
    const result = await getUser(uid);
    return result.success && !!result.data;
  } catch (error) {
    console.error('🚫 Error checking user existence:', error);
    return false;
  }
};

/**
 * Delete user account (for GDPR compliance)
 */
export const deleteUser = async (uid: string): Promise<FirebaseServiceResponse<boolean>> => {
  try {
    console.log('🗑️ Deleting user:', uid);
    
    // Note: In a real implementation, you'd also need to delete all user rounds
    // and potentially anonymize data rather than delete for analytics
    
    const result = await updateDocument(
      FIREBASE_COLLECTIONS.USERS,
      uid,
      {
        deleted: true,
        deletedAt: serverTimestamp()
      }
    );
    
    if (result.success) {
      console.log('✅ User marked as deleted:', uid);
      return {
        success: true,
        data: true
      };
    } else {
      console.error('🚫 Failed to delete user:', result.error);
      return {
        success: false,
        error: {
          code: 'user/deletion-failed',
          message: result.error || 'Failed to delete user'
        }
      };
    }
  } catch (error) {
    console.error('🚫 Error deleting user:', error);
    return {
      success: false,
      error: {
        code: 'user/deletion-error',
        message: error instanceof Error ? error.message : 'Unknown error deleting user'
      }
    };
  }
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('👤 User service initialized in development mode');
}
